define([
  "require",
  "jquery"
], function (require, $) {

  var pluginLoader = {
    DEBUG: Boolean(window.localStorage.getItem('DEBUG')) || false,
    _components: {}, // JQuery cached list of component elements found on page
    _plugins: {}, // Cached list of unique plugin references found on page

    /**
     * Creates a new deferred object we can attach debug loggers to
     *
     * @return {Promise} a jQuery deferred promise 
     */
    deferred: function () {
      var self = this;
      var logStyles = [
        'color: #0071B5;',
        'text-transform: uppercase;'
      ];
      return new $.Deferred().done(function (msg) {
        if (self.DEBUG && msg) console.log('%cpluginLoader => ', logStyles.join(';'), msg);
      }).fail(function (error) {
        if (error) console.error(error);
      }).progress(function (msg) {
        // Log console warnings for progress notifications
        if (self.DEBUG && msg) console.warn(msg);
      });
    },

    /**
     * Standardized loader method for explicit loading that follow the JQuery plugin pattern
     * If the necessary elements don't exist on the page, the plugin won't be loaded
     *
     * //TODO - Everything using this should be refactored to leverage data attributes so
     * they can be loaded automatically and faster with only a single DOM query
     *
     * @param {object|array} plugins A single plugin or an array of plugins to load
     *	 Each plugin in the array follows this syntax:
     *    {
     *       name: 'myPlugin',               // Short Name for the module as defined in the manifest
     *       initFunction: 'myPluginInit',   // Name of the method called to instantiate the JQuery plugin. If not provided, the module will be required, but no function run
     *       selector: '.myplugin',          // JQuery selector to find the page elements to which the plugin attaches 
     *       context: $('.parentElement'),	 // Optional JQuery object to use as the context for the selector
     *       options: {example:'example'},   // Optional set of configurations to pass to the plugin
     *       callback: 'myCallbackFunction'  // Optional callback to run after initializing the plugin
     *    }
     *
     * @returns {array} list of promises for plugins that we attempted to load.
     **/
    loadPlugins: function (plugins) {
      // If provided a single item, convert to an array
      plugins = Array.isArray(plugins) ? plugins : [plugins];
      var self = this;
      return plugins.map(function (plugin) {
        return self.loadPlugin(plugin);
      });
    },

    /**
     * Loaads an individual plugin
     *
     * @param {object} plugin A plugin object defined above
     * @returns {Promise} a jQuery deferred promise 
     */
    loadPlugin: function (plugin) {
      var self = this,
        context = plugin.context || null,
        initFunction = plugin.initFunction || null,
        options = plugin.options || null,
        callback = plugin.callback || null,
        $el = plugin.$el || $(plugin.selector, context),
        deferred = self.deferred(),
        msg;

      if (!$el.length) {
        return deferred.notify(
          'Tried to run component ' + plugin.name + ', but no elements found.'
        ).resolve(false);
      }
      // Try to load the plugin in
      require([plugin.name], function (pluginInsatnce) {
        var plgObj = pluginInsatnce;
        // Initialize the plugin against the JQuery object
        if (initFunction) {
          // Verfiy a JQuery plugin was registered
          if (typeof $.fn[initFunction] !== 'function') {
            deferred.reject(new Error(initFunction + ' is not defined as a JQuery plugin'));
          } else {
            // Call the plugin function
            var $plugin = $el[initFunction](options);
            // jQuery plugins don't return an instance when imported
            // They need to be initialized first
            plgObj = plgObj || $plugin;
            msg = plugin.name + ' successfully loaded and initialized';
          }
        } else {
          msg = plugin.name + ' successfully loaded';
        }
        // Run the callback if one is provided
        if (callback) {
          if (typeof callback !== 'function') {
            deferred.reject(new Error(callback + ' is not a function.'));
          } else {
            msg += '\n' + plugin.name + ' callback successfully called';
            callback($el, plgObj, options);
          }
        }
        deferred.resolve(msg, plgObj);
      }, function (error) {
        // Catch requirejs loading errors
        deferred.reject(error);
      });
      return deferred;
    },

    /**
     * Search the DOM for any component instances and add them to the cached list
     * 
     * @param {object} [$scope] - jQuery object to search. Scope element itself is also checked
     **/
    findComponents: function ($scope) {
      $scope = $scope || $(document);
      var self = this;
      var $components = $scope.find('[data-vfdp]').addBack('[data-vfdp]'); // addBack includes the $scope in the possible results
      var plugins = [];

      // Extract the list of plugins from the list of components
      $components.each(function (idx) {
        plugins.push($(this).data('vfdp'));
      });

      // Filter to only unique plugins
      plugins = plugins.filter(function (value, index, el) {
        return el.indexOf(value) === index;
      });

      // Cache results so we can reduce DOM queries
      self._plugins = plugins;
      self._components = $components;
      return {
        plugins: plugins,
        components: $components,
      };
    },

    /**
     * Dynamically loads and initializes components that were already found on the page.
     * Uses a cached selector of Components for best performance.
     *
     * @param {string|array} [plugins] - Optional list of plugins to attempt to run. Uses already cached list of plugins when not specified.
     * @param {object} [components] - Optional JQuery list of components to run against. Uses already cached list of components when not specified.
     */
    runComponents: function (plugins, components) {
      var self = this;
      plugins = plugins || self._plugins;
      components = components || self._components;

      // Convert string to array of one
      if (typeof plugins === 'string') {
        plugins = [plugins];
      }

      if (plugins.length <= 0 || components.length <= 0) {
        return [
          self.deferred().notify(
            'Could not run components. No component instances specified and none found in selector cache.'
          ).resolve(false)
        ];
      } else {
        return plugins.map(function (plugin) {
          return self.loadPlugin({
            name: 'vfdp.' + plugin,
            initFunction: plugin,
            $el: components.filter('[data-vfdp="' + plugin + '"]')
          });
        });
      }
    },

    /**
     * Finds all VFDP Component instances on the specified scope and runs their plugins
     *
     * @param {object} [$scope] - Optional JQuery object to search for component instances. Defaults to $(document)
     **/
    runVFDPPlugins: function ($scope) {
      $scope = $scope || $(document);
      var self = this;
      self.findComponents($scope);
      // return a promise from an array of promises from initialized components
      return $.when.apply(null, self.runComponents());
    },

    /**
     * Binds runVFDPPlugins to an event; will run with event.target as the scope.
     * 
     * @param {string} eventName - The name of the event to bind to; please use a unique suffix so that
     *                             this method can ensure the event isn't bound multiple times (i.e. open.vfdp-plugin-loader) 
     **/
    bindRunVFDPPluginsToEvent: function (eventName) {
      var self = this;
      var $document = $(document);
      $document.off(eventName);
      $document.on(eventName, function (event) {
        return self.runVFDPPlugins($(event.target));
      });
    },

    /**
     * Sets up events for dynamically loaded content to run plugins (i.e. when opening modals)
     * 
     * @param {string} [eventName] - event to bind initializer handler
     **/
    initVFDPPluginEvents: function (eventName) {
      eventName = eventName || 'open.zf.reveal.vfdp-plugin-loader';
      var self = this;

      self.bindRunVFDPPluginsToEvent(eventName);
    }
  };

  return pluginLoader;
});
